import { makeStyles } from "@mui/styles";
import { TextField } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { MOBILE_WIDTH } from "../App";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import useWindowSize from "../hooks/useWindowSize";
import { TimeOffDateFilters, GanttDateFilters } from "../utils/enums/filters/TimeOffDateFilters";
import { TimeOffStatusFilters } from "../utils/enums/filters/TimeOffStatusFilters";
import RoleUtils from "../utils/RoleUtils";
import { AppToolbar, DatePickerComponent, DatePickerWrapper, MAX_DATE, MIN_DATE } from "./AppToolbar";
import { ButtonComponent } from "./ButtonComponent";
import FilterDialog, { FilterDialogButton } from "./FilterDialog";
import { SelectComponent } from "./SelectComponent";
import { SimpleAutoComplete } from "./SimpleAutoComplete";
import { useHistory, useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { filtersBarStyle, Token } from "../theme";
import { Theme } from "@mui/material/styles";

interface Props {
  headerActions?: React.ReactNode;
  searchFilter: string;
  statusFilter: string;
  dateFilter: string;
  projects: AutoCompleteUIDTO[];
  projectSearch: AutoCompleteUIDTO;
  isChartOpen: boolean;
  handleSearchFilter: (searchString: string) => void;
  handleStatusFilter: (status: string) => void;
  handleDateFilter: (date: string) => void;
  handleProjectFilter: (project: AutoCompleteUIDTO) => void;
  onToggleChart: () => void;
  isGanttGraphEnabled: boolean;
  month: Date | null;
  setMonth: (date: Date | null) => void;
  year: Date | null;
  setYear: (date: Date | null) => void;
}
export default function TimeOffSearchFilters(props: Props) {
  const searchParams = new URLSearchParams(useLocation().search);
  const classes = useStyles();
  const { isMobileWidth } = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const project = searchParams.get("project");
  const { updateUrl } = useUpdateUrl(Routes.TIME_OFF, searchParams);

  const resetFilters = () => {
    props.handleStatusFilter("All");
    props.handleDateFilter(TimeOffDateFilters.ACTIVE);
    props.setYear(null);
    props.setMonth(null);
    props.handleSearchFilter("");
  };

  const renderMonthAndYearInputs = () => {
    return (
      <DatePickerWrapper>
        <DatePickerComponent
          className={"datePicker " + classes.date}
          views="month"
          label="Month"
          value={props.month}
          inputFormat="MMMM"
          onChange={(date) => {
            props.setMonth(date);
            date && searchParams.set("month", (date!.getMonth() + 1).toString());
            updateUrl();
          }}
          style={{ margin: "0", textAlignLast: "left" }}
        />

        <DatePickerComponent
          className={"datePicker " + classes.date}
          views="year"
          label="Year"
          value={props.year}
          inputFormat="yyyy"
          style={{ marginRight: "0", textAlignLast: "left" }}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          onChange={(date) => {
            props.setYear(date);
            date && searchParams.set("year", date!.getFullYear().toString());
            updateUrl();
          }}
        />
      </DatePickerWrapper>
    );
  };

  const renderStatusInput = () => {
    return (
      <SelectComponent
        label={"Status"}
        value={props.statusFilter}
        setValue={(status: string) => {
          props.handleStatusFilter(status);
          searchParams.set("status", status);
          updateUrl();
        }}
        options={Object.values(TimeOffStatusFilters)}
        size="small"
      />
    );
  };

  const renderProjectNameAndUserNameInputs = () => {
    const allOption = { label: "All", externalId: "All" };
    const projectInList = props.projects.find((p) => p.externalId === project);
    const selectedProject: AutoCompleteUIDTO = project && projectInList ? projectInList : allOption;

    return (
      <>
        <TextField
          className={classes.textField}
          label="User name"
          value={props.searchFilter}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            props.handleSearchFilter(e.target.value);
            e.target.value === "" ? searchParams.delete("search") : searchParams.set("search", e.target.value);
            updateUrl();
          }}
          autoComplete="off"
          size="small"
          disabled={props.isChartOpen}
        />
        <SimpleAutoComplete
          className={classes.textField}
          label="Project Name"
          items={[allOption, ...props.projects]}
          value={selectedProject}
          onChange={(newValue) => {
            props.handleProjectFilter(newValue);
            searchParams.set("project", newValue.externalId);
            updateUrl();
          }}
          disablePortal
          readonly={props.isChartOpen}
          disabled={props.isChartOpen}
        />
      </>
    );
  };

  const renderDateTypeInput = () => {
    return (
      <SelectComponent
        label={"Date"}
        value={props.isChartOpen ? GanttDateFilters.ACTIVE : props.dateFilter}
        setValue={(date: string) => {
          props.handleDateFilter(date);
          searchParams.set("date", date);
          updateUrl();
        }}
        options={props.isChartOpen ? Object.values(GanttDateFilters) : Object.values(TimeOffDateFilters)}
        size="small"
        readonly={props.isChartOpen}
        disabled={props.isChartOpen}
      />
    );
  };

  const renderButtonShowProjectChart = () => {
    const disabled = !props.projectSearch || props.projectSearch.label === "All";

    return (
      <ButtonComponent
        variant="outlined"
        style={{
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          height: 34,
          padding: "0 14px",
          borderColor: Token.borderDefault,
          backgroundColor: Token.surfaceCard,
          color: disabled ? Token.textTertiary : Token.textPrimary,
        }}
        onClick={props.onToggleChart}
        text={`${props.isChartOpen ? "hide" : "show"} project chart`}
        disabled={disabled}
      />
    );
  };
  const renderDesktopFilters = () => {
    return (
      <>
        {renderStatusInput()}

        {renderDateTypeInput()}

        {renderMonthAndYearInputs()}

        {RoleUtils.isAdminOrManager() && (
          <>
            {renderProjectNameAndUserNameInputs()}

            {renderButtonShowProjectChart()}
          </>
        )}
      </>
    );
  };

  const renderMobileFilters = () => {
    return RoleUtils.isAdminOrManager() ? (
      <>
        {renderStatusInput()}

        {renderButtonShowProjectChart()}

        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </>
    ) : (
      <>
        {renderStatusInput()}

        {renderDateTypeInput()}

        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </>
    );
  };

  const renderMobileDialog = () => {
    return (
      <>
        {renderMonthAndYearInputs()}

        {RoleUtils.isAdminOrManager() && (
          <>
            {renderDateTypeInput()}
            {renderProjectNameAndUserNameInputs()}
          </>
        )}

        <ButtonComponent
          style={{ backgroundColor: Token.brandAccent }}
          text="Reset filters"
          onClick={() => {
            resetFilters();
          }}
        />
      </>
    );
  };

  return (
    <>
      <AppToolbar title="Timeoff requests" rightContent={props.headerActions}>
        <div className={RoleUtils.isAdminOrManager() ? classes.containerAdmin : classes.container}>
          {isMobileWidth() ? renderMobileFilters() : renderDesktopFilters()}
        </div>
        <FilterDialog title={"Timeoff"} setIsOpen={setIsOpen} isOpen={isOpen}>
          {renderMobileDialog()}
        </FilterDialog>
      </AppToolbar>
    </>
  );
}

const useStyles = makeStyles<Theme, {}, string>(
  // @ts-ignore -- computed media-query keys are valid JSS but not StyleRules
  (theme) => ({
    container: {
      ...filtersBarStyle(),
      "& > div, & > button": {
        margin: "0 !important",
      },
      "& button": {
        minWidth: "fit-content",
      },
      "&> div:first-child": {
        minWidth: "150px",
      },
    },
    containerAdmin: {
      ...filtersBarStyle(),
      flexWrap: "nowrap",
      "&> div": {
        flex: "1 1 0",
        minWidth: 0,
      },
      "&> button": {
        flex: "0 0 auto",
        alignSelf: "flex-end",
      },
      "&  .datePicker": {
        flex: "1 1 0",
        minWidth: "0 !important",
        width: "auto !important",
      },
      "& > div, & > button": {
        margin: "0 !important",
      },
      "&> div:first-child": {
        minWidth: "150px",
      },
    },
    textField: {
      width: "12rem !important",
      minWidth: "10rem !important",
    },
    [theme.breakpoints.down(1450)]: {
      container: {
        display: "flex",
        alignItems: "flex-end",
        flexWrap: "nowrap",
        columnGap: "10px",
        "&> div": {
          flex: "1 1 0",
          minWidth: 0,
        },
        "& button": {
          flex: "0 0 auto",
          width: "fit-content",
        },
        "&  .datePicker": {
          minWidth: "0 !important",
          width: "100% !important",
        },
      },
      containerAdmin: {
        display: "flex",
        alignItems: "flex-end",
        flexWrap: "nowrap",
        columnGap: "10px",
        "&> div": {
          flex: "1 1 0",
          minWidth: 0,
        },
        "& button": {
          flex: "0 0 auto",
          width: "fit-content",
        },
        "&  .datePicker": {
          minWidth: "0 !important",
          width: "100% !important",
        },
      },
    },
    [theme.breakpoints.down("md")]: {
      container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridRowGap: "10px",
        "&> div:nth-child(4)": {
          width: "100%",
        },
        "& button": {
          width: "fit-content",
          "grid-column": "1/span 2",
        },
        "&  .datePicker": {
          minWidth: "100% !important",
          width: "100% !important",
        },
      },
      containerAdmin: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridRowGap: "10px",
        "&> div:nth-child(4)": {
          width: "100%",
        },
        "& button": {
          width: "fit-content",
          "grid-column": "1/span 2",
        },
        "&  .datePicker": {
          minWidth: "100% !important",
          width: "100% !important",
        },
      },
    },
    [theme.breakpoints.down(1100)]: {
      container: {
        marginBottom: "50px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridRowGap: "10px",
        "&> div:nth-child(4)": {
          width: "100%",
        },
        "& button": {
          width: "fit-content",
          "grid-column": "1/span 2",
        },
        "&  .datePicker": {
          minWidth: "100% !important",
          width: "100% !important",
        },
      },
      containerAdmin: {
        marginBottom: "50px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridRowGap: "10px",
        "&> div:nth-child(4)": {
          width: "100%",
        },
        "& button": {
          width: "fit-content",
          "grid-column": "1/span 2",
        },
        "&  .datePicker": {
          minWidth: "100% !important",
          width: "100% !important",
        },
      },
    },
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
      containerAdmin: {
        display: "grid",
        gridTemplateColumns: ".8fr 1.2fr",
        gridRowGap: "10px",
        "& > button": {
          fontSize: "11px",
        },
      },
      textField: {
        width: "100% !important",
      },
      container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridRowGap: "10px",
      },
      date: {
        width: "100% !important",
        maxWidth: "100% !important",
      },
    },
    [theme.breakpoints.down(720)]: {
      containerAdmin: {
        "& > button": {
          width: "100%",
        },
        paddingBottom: ".5em",
      },
    },
    [theme.breakpoints.down(460)]: {
      containerAdmin: {
        "&> .chart-button": {
          fontSize: "11px",
          padding: "5px 8px",
        },
      },
    },
  }),
  { index: 1 },
);
